import React, { useMemo } from "react";
import { FilterIcon } from "@omega/icons";
import { List, type ListProps } from "./List";
import type { UseBusinessListQueryResult } from "@src/hooks/useBusinessListQuery";

/**
 * Пропсы {@link BusinessList}.
 * @template TFilter тип фильтра списка
 */
export type BusinessListProps<TFilter extends object = Record<string, unknown>> = Omit<ListProps, "isLoad" | "idle"> & {
    /**
     * Результат {@link useBusinessListQuery}: по умолчанию `isLoad` и `idle` берутся из `isFetching` / `filter`.
     * Без `list` можно передать `isLoad` и `idle` явно.
     */
    list?: Pick<UseBusinessListQueryResult<unknown, TFilter>, "isFetching" | "filter">;
    /** Явная загрузка; по умолчанию — `list.isFetching` или `false`. */
    isLoad?: boolean;
    /**
     * Список ещё не запрашивался; по умолчанию — `list.filter == null` или `false`.
     * Полезно при кастомном `enabled` в {@link useBusinessListQuery}.
     */
    idle?: boolean;
    /**
     * Открыть фильтр из idle-состояния. \
     * Если `idleAction` не передан — создаёт кнопку «К фильтру» с иконкой поиска.
     */
    onOpenFilter?: () => void;
    /** Подпись кнопки фильтра в idle-состоянии. @default "К фильтру" */
    filterActionText?: string;
};

/**
 * Стандартный список для business-экранов: loading, idle/empty и опциональная кнопка «К фильтру».
 */
export function BusinessList<TFilter extends object = Record<string, unknown>>({
    list,
    isLoad: isLoadProp,
    idle: idleProp,
    onOpenFilter,
    filterActionText = "К фильтру",
    idleAction,
    ...listProps
}: BusinessListProps<TFilter>) {
    const isLoad = isLoadProp ?? list?.isFetching ?? false;
    const idle = idleProp ?? (list != null ? list.filter == null : false);

    const resolvedIdleAction = useMemo(() => {
        if (idleAction != null) return idleAction;
        if (onOpenFilter == null) return undefined;
        return {
            text: filterActionText,
            icon: <FilterIcon size={16} />,
            onClick: onOpenFilter,
        };
    }, [idleAction, onOpenFilter, filterActionText]);

    return <List {...listProps} isLoad={isLoad} idle={idle} idleAction={resolvedIdleAction} />;
}
